import React, { useMemo } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

const AlertRuleFilterBar = ({ 
  meters, 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  sources,
  currentFinancialYear 
}) => {
  const getKpi = (m) =>
    m.financialYearKpis?.find((f) => f.financialYear === currentFinancialYear)?.kpi;

  const sharedSelectProps = {
    className: 'react-select-container',
    classNamePrefix: 'react-select',
    isClearable: true,
    closeMenuOnSelect: false,
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  // Get sub-locations from selected sources
  const availableSubLocations = useMemo(() => {
    if (filters.sourceIds.length === 0) return [];
    
    const selectedSources = sources.filter(s => filters.sourceIds.includes(s.value));
    const subLocs = selectedSources.flatMap(s => s.subLocations || []);
    
    return Array.from(new Map(subLocs.map(sl => [sl.value, sl])).values());
  }, [filters.sourceIds, sources]);

  const availableModules = useMemo(() => {
    let filteredMeters = meters;
    if (filters.sourceIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subLocationIds.includes(m.subLocationId));

    const modules = new Set();
    filteredMeters.forEach(m => { if (getKpi(m)?.module) modules.add(getKpi(m).module); });
    return Array.from(modules).map(m => ({ value: m, label: m }));
  }, [meters, filters.sourceIds, filters.subLocationIds, currentFinancialYear]);

  const availableCategories = useMemo(() => {
    let filteredMeters = meters;
    if (filters.sourceIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subLocationIds.includes(m.subLocationId));
    if (filters.modules.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.modules.includes(getKpi(m)?.module));

    const categories = new Set();
    filteredMeters.forEach(m => { if (getKpi(m)?.category) categories.add(getKpi(m).category); });
    return Array.from(categories).map(c => ({ value: c, label: c }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, currentFinancialYear]);

  const availableSubCategories = useMemo(() => {
    let filteredMeters = meters;
    if (filters.sourceIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subLocationIds.includes(m.subLocationId));
    if (filters.modules.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.modules.includes(getKpi(m)?.module));
    if (filters.categories.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.categories.includes(getKpi(m)?.category));

    const subCategories = new Set();
    filteredMeters.forEach(m => { if (getKpi(m)?.subCategory) subCategories.add(getKpi(m).subCategory); });
    return Array.from(subCategories).map(sc => ({ value: sc, label: sc }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, currentFinancialYear]);

  const availableKpis = useMemo(() => {
    let filteredMeters = meters;
    if (filters.sourceIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subLocationIds.includes(m.subLocationId));
    if (filters.modules.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.modules.includes(getKpi(m)?.module));
    if (filters.categories.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.categories.includes(getKpi(m)?.category));
    if (filters.subCategories.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subCategories.includes(getKpi(m)?.subCategory));

    const kpis = new Set();
    filteredMeters.forEach(m => { if (getKpi(m)?.kpi) kpis.add(getKpi(m).kpi); });
    return Array.from(kpis).map(k => ({ value: k, label: k }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, currentFinancialYear]);

  const availableMeterIds = useMemo(() => {
    let filteredMeters = meters;
    if (filters.sourceIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subLocationIds.includes(m.subLocationId));
    if (filters.modules.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.modules.includes(getKpi(m)?.module));
    if (filters.categories.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.categories.includes(getKpi(m)?.category));
    if (filters.subCategories.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.subCategories.includes(getKpi(m)?.subCategory));
    if (filters.kpis.length > 0)
      filteredMeters = filteredMeters.filter(m => filters.kpis.includes(getKpi(m)?.kpi));

    const meterMap = new Map();
    filteredMeters.forEach(m => {
      if (m.meterId && !meterMap.has(m.meterId))
        meterMap.set(m.meterId, m.meterName || m.meterId);
    });
    return Array.from(meterMap.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, filters.kpis, currentFinancialYear]);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    if (field === 'sourceIds' && value.length === 0) newFilters.subLocationIds = [];
    onFiltersChange(newFilters);
  };

  const hasActiveFilters = 
    filters.sourceIds.length > 0 ||
    filters.subLocationIds.length > 0 ||
    filters.modules.length > 0 ||
    filters.categories.length > 0 ||
    filters.subCategories.length > 0 ||
    filters.kpis.length > 0 ||
    filters.meterIds.length > 0 ||
    filters.enabledStatus !== null;

  return (
    <Card className="filter-card mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filters
          </h6>
          {hasActiveFilters && (
            <Button variant="outline-secondary" size="sm" onClick={onClearFilters}>
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Clear All
            </Button>
          )}
        </div>

        <Row>
          <Col md={4} className="mb-3">
            <Form.Label>Location</Form.Label>
            <Select
              isMulti
              options={sources}
              value={sources.filter(s => filters.sourceIds.includes(s.value))}
              onChange={(selected) => handleFilterChange('sourceIds', selected.map(s => s.value))}
              placeholder="All locations..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Sub Location</Form.Label>
            <Select
              isMulti
              options={availableSubLocations}
              value={availableSubLocations.filter(sl => filters.subLocationIds.includes(sl.value))}
              onChange={(selected) => handleFilterChange('subLocationIds', selected.map(sl => sl.value))}
              placeholder="All sub locations..."
              isDisabled={availableSubLocations.length === 0}
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Module</Form.Label>
            <Select
              isMulti
              options={availableModules}
              value={availableModules.filter(m => filters.modules.includes(m.value))}
              onChange={(selected) => handleFilterChange('modules', selected.map(m => m.value))}
              placeholder="All modules..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Category</Form.Label>
            <Select
              isMulti
              options={availableCategories}
              value={availableCategories.filter(c => filters.categories.includes(c.value))}
              onChange={(selected) => handleFilterChange('categories', selected.map(c => c.value))}
              placeholder="All categories..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Sub Category</Form.Label>
            <Select
              isMulti
              options={availableSubCategories}
              value={availableSubCategories.filter(sc => filters.subCategories.includes(sc.value))}
              onChange={(selected) => handleFilterChange('subCategories', selected.map(sc => sc.value))}
              placeholder="All sub categories..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>KPI</Form.Label>
            <Select
              isMulti
              options={availableKpis}
              value={availableKpis.filter(k => filters.kpis.includes(k.value))}
              onChange={(selected) => handleFilterChange('kpis', selected.map(k => k.value))}
              placeholder="All KPIs..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Meter ID</Form.Label>
            <Select
              isMulti
              options={availableMeterIds}
              value={availableMeterIds.filter(m => filters.meterIds.includes(m.value))}
              onChange={(selected) => handleFilterChange('meterIds', selected.map(m => m.value))}
              placeholder="All meters..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.enabledStatus === null ? 'all' : filters.enabledStatus.toString()}
              onChange={(e) => {
                const value = e.target.value === 'all' ? null : e.target.value === 'true';
                handleFilterChange('enabledStatus', value);
              }}
            >
              <option value="all">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </Form.Select>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default AlertRuleFilterBar;